import { useState } from 'react';
import './InstallerSimulator.css';

/**
 * Generic "fake installer window" checkbox simulation. Reusable for any
 * future checkbox_sim question - just pass a different windowTitle/heading/
 * checkboxes list. Nothing here is specific to the Python installer example.
 *
 * @param {string} windowTitle    e.g. "Python 3.14.6 Setup"
 * @param {string} heading        e.g. "Install Python 3.14.6 (64-bit)"
 * @param {{label:string, required?:boolean, warning?:boolean, hint?:string}[]} checkboxes
 * @param {string} submitLabel        e.g. "Install now"
 * @param {string} submitErrorMessage shown if submit is attempted while invalid
 * @param {boolean} disabled          true once the student has already answered
 * @param {(checkedIndices:number[]) => void} onSubmit
 * @param {number[]|null} revealCorrectIndices  teacher-view "answer key" mode: pre-checks
 *   and badges these indices, and implies disabled/hideSubmit (used for the host's
 *   projector reveal, never for the student's own answering flow).
 * @param {boolean} hideSubmit  hide the submit button entirely (host preview, where
 *   nothing is actually being submitted)
 */
export default function InstallerSimulator({
  windowTitle,
  heading,
  checkboxes,
  submitLabel = 'Install now',
  disabled = false,
  onSubmit,
  revealCorrectIndices = null,
  hideSubmit = false,
}) {
  const [checked, setChecked] = useState(() => new Set(revealCorrectIndices || []));

  const toggle = (i) => {
    if (disabled) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleSubmitClick = () => {
    if (disabled) return;
    onSubmit([...checked]);
  };

  return (
    <div className="installer-window">
      <div className="installer-titlebar">
        <div className="installer-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <span className="installer-title">{windowTitle}</span>
      </div>

      <div className="installer-body">
        <h3 className="installer-heading">{heading}</h3>

        <ul className="installer-checklist">
          {checkboxes.map((cb, i) => {
            const isAnswerKey = revealCorrectIndices?.includes(i);
            return (
              <li key={i} className={`installer-check-row ${isAnswerKey ? 'is-answer-key' : ''}`}>
                <label className="installer-check-label">
                  <input
                    type="checkbox"
                    checked={checked.has(i)}
                    onChange={() => toggle(i)}
                    disabled={disabled}
                  />
                  <span>{cb.label}</span>
                </label>
                {isAnswerKey && <span className="installer-required-badge">✓ ต้องติ๊กข้อนี้</span>}
              </li>
            );
          })}
        </ul>

        {!hideSubmit && (
          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={disabled}
            onClick={handleSubmitClick}
          >
            {submitLabel}
          </button>
        )}
      </div>
    </div>
  );
}
